import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import VoiceVisualizer from "@/components/VoiceVisualizer";
import { toast } from "sonner";
import { 
  Mic, MicOff, Square, Phone, PhoneOff, Loader2,
  MessageSquare, X, AlertCircle, RefreshCw, WifiOff, Monitor
} from "lucide-react";

const CONNECTION_TIMEOUT = 30000;
const MAX_RECONNECT_ATTEMPTS = 3;

const ERROR_MESSAGES = {
  microphone_denied: "Microphone access denied. Please allow microphone access and try again.",
  microphone_not_found: "No microphone found. Please connect a microphone and try again.",
  network_error: "Network connection lost. Please check your internet connection.",
  timeout: "Connection timed out. The server may be busy, please try again.",
  ice_failed: "Connection failed. This might be a network issue - try reconnecting.",
  openai_error: "Failed to connect to voice service. Please try again.",
  session_expired: "Your session has expired. Please start a new roleplay.",
  unknown: "An unexpected error occurred. Please try again."
};

async function safeFetch(url, options = {}) {
  const response = await fetch(url, options);
  let text = "";
  try { text = await response.text(); } catch { text = "{}"; }
  return {
    ok: response.ok, status: response.status, text,
    json: () => { try { return JSON.parse(text); } catch { return {}; } }
  };
}

export default function ActiveRoleplay({ isEmbedded = false }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const sessionId = params.sessionId;
  const embedId = params.embedId;
  
  const scenarioFromState = location.state?.scenario;
  const scenarioIdFromState = location.state?.scenarioId;
  const screenSharingModeFromState = location.state?.screenSharingMode || false;
  const embedIdFromState = location.state?.embedId || embedId;
  
  const [scenario, setScenario] = useState(scenarioFromState);
  const [rpSessionId, setRpSessionId] = useState(!isEmbedded && sessionId !== "new" ? sessionId : null);
  const [connectionState, setConnectionState] = useState("disconnected");
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showTranscript, setShowTranscript] = useState(true);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionProgress, setConnectionProgress] = useState(0);
  const [pushToTalkMode, setPushToTalkMode] = useState(isEmbedded ? false : screenSharingModeFromState);
  const [isPushingToTalk, setIsPushingToTalk] = useState(false);

  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const localStreamRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const initStartedRef = useRef(false);
  const mountedRef = useRef(true);
  const connectionTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const unmutDelayRef = useRef(null);
  const speechDetectedRef = useRef(false);
  const pttStartTimeRef = useRef(null);
  const handleEndRoleplayRef = useRef(null);
  const disconnectCallRef = useRef(null);
  const isUserSpeakingRef = useRef(false);
  const isPushingToTalkRef = useRef(false);
  const pushToTalkModeRef = useRef(isEmbedded ? false : screenSharingModeFromState);
  const pendingHangupRef = useRef(false);

  // Audio recording refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);

  // Message ordering refs
  const pendingAITranscriptRef = useRef({});
  const pendingUserOrderRef = useRef(null);
  const messageOrderRef = useRef(0);

  const setMicEnabled = useCallback((enabled) => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = enabled;
      }
    }
  }, []);

  const delayedUnmute = useCallback(() => {
    if (pushToTalkModeRef.current && !isPushingToTalkRef.current) return;
    if (unmutDelayRef.current) clearTimeout(unmutDelayRef.current);
    unmutDelayRef.current = setTimeout(() => {
      if (!isMuted && mountedRef.current && (!pushToTalkModeRef.current || isPushingToTalkRef.current)) {
        // Clear any residual echo from AI speech before enabling mic
        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        }
        setMicEnabled(true);
      }
    }, 700);
  }, [isMuted, setMicEnabled]);

  const handlePushToTalkStart = useCallback(() => {
    if (!pushToTalkMode || isMuted) return;
    setIsPushingToTalk(true);
    isPushingToTalkRef.current = true;
    speechDetectedRef.current = false;
    pttStartTimeRef.current = Date.now();
    // Pre-assign user order number since speech_started won't fire with VAD disabled
    if (pendingUserOrderRef.current === null) {
      pendingUserOrderRef.current = messageOrderRef.current++;
    }
    setMicEnabled(true);
    if (dataChannelRef.current?.readyState === "open") {
      dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
    }
  }, [pushToTalkMode, isMuted, setMicEnabled]);

  const handlePushToTalkEnd = useCallback(() => {
    if (!pushToTalkMode) return;
    setIsPushingToTalk(false);
    isPushingToTalkRef.current = false;
    setMicEnabled(false);
    
    const holdTime = pttStartTimeRef.current ? Date.now() - pttStartTimeRef.current : 0;
    const minHoldTime = 300;
    
    // In PTT mode with server VAD disabled, speech_started events don't fire.
    // So commit based on hold time alone — if held long enough, assume speech occurred.
    if (holdTime >= minHoldTime && dataChannelRef.current?.readyState === "open") {
      setTimeout(() => {
        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
          // Manually trigger response since server VAD is disabled in PTT mode
          dataChannelRef.current.send(JSON.stringify({
            type: "response.create",
            response: { output_modalities: ["audio"] }
          }));
        }
      }, 100);
    } else {
      if (dataChannelRef.current?.readyState === "open") {
        dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
      }
    }
    pttStartTimeRef.current = null;
  }, [pushToTalkMode, setMicEnabled]);

  // Audio recording: start recording mixed audio
  const startRecording = useCallback((localStream, remoteStream) => {
    try {
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();

      // Mix local mic audio
      if (localStream) {
        const localSource = ctx.createMediaStreamSource(localStream);
        localSource.connect(dest);
      }
      // Mix remote AI audio
      if (remoteStream) {
        const remoteSource = ctx.createMediaStreamSource(remoteStream);
        remoteSource.connect(dest);
      }

      const recorder = new MediaRecorder(dest.stream, { mimeType: "audio/webm;codecs=opus" });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start(1000); // Collect chunks every second
      mediaRecorderRef.current = recorder;
    } catch (err) {
      console.warn("Audio recording not available:", err.message);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const uploadRecording = useCallback(async (sessionId) => {
    if (audioChunksRef.current.length === 0) return;
    try {
      const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      if (blob.size < 1000) return; // Skip tiny recordings
      
      const formData = new FormData();
      formData.append("file", blob, `${sessionId}.webm`);
      
      await fetch(`${API}/recordings/upload/${sessionId}`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
    } catch (err) {
      console.warn("Recording upload failed:", err.message);
    } finally {
      audioChunksRef.current = [];
    }
  }, []);

  // Online/offline monitoring
  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); toast.success("Back online"); };
    const handleOffline = () => {
      setIsOnline(false);
      setError(ERROR_MESSAGES.network_error);
      setErrorType("network_error");
      toast.error("Connection lost");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (unmutDelayRef.current) clearTimeout(unmutDelayRef.current);
    };
  }, []);

  const cleanup = useCallback(() => {
    if (connectionTimeoutRef.current) { clearTimeout(connectionTimeoutRef.current); connectionTimeoutRef.current = null; }
    if (reconnectTimeoutRef.current) { clearTimeout(reconnectTimeoutRef.current); reconnectTimeoutRef.current = null; }
    if (unmutDelayRef.current) { clearTimeout(unmutDelayRef.current); unmutDelayRef.current = null; }
    
    stopRecording();
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (dataChannelRef.current) { dataChannelRef.current.close(); dataChannelRef.current = null; }
    if (peerConnectionRef.current) { peerConnectionRef.current.close(); peerConnectionRef.current = null; }
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current.parentNode?.removeChild(audioElementRef.current);
      audioElementRef.current = null;
    }
    setConnectionProgress(0);
  }, [stopRecording]);

  const handleConnectionError = useCallback((errorMsg, type = "unknown") => {
    const friendlyMessage = ERROR_MESSAGES[type] || errorMsg || ERROR_MESSAGES.unknown;
    setError(friendlyMessage);
    setErrorType(type);
    setConnectionState("disconnected");
    setConnectionProgress(0);
    
    if (connectionTimeoutRef.current) { clearTimeout(connectionTimeoutRef.current); connectionTimeoutRef.current = null; }
    
    const autoReconnectErrors = ["ice_failed", "network_error", "timeout"];
    if (autoReconnectErrors.includes(type) && reconnectAttempts < MAX_RECONNECT_ATTEMPTS && isOnline) {
      const delay = Math.min(2000 * (reconnectAttempts + 1), 6000);
      toast.info("Reconnecting...", { description: `Attempt ${reconnectAttempts + 1} of ${MAX_RECONNECT_ATTEMPTS}` });
      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) { setReconnectAttempts(prev => prev + 1); reconnectCall(); }
      }, delay);
    } else {
      toast.error("Connection failed", { description: friendlyMessage });
    }
    initStartedRef.current = false;
    // reconnectCall is a stable forward reference defined below; intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectAttempts, isOnline]);

  const handleRealtimeEvent = useCallback((event) => {
    if (!mountedRef.current) return;

    switch (event.type) {
      case "session.created":
        toast.success("Connected!", { description: "Voice call started" });
        break;

      case "input_audio_buffer.speech_started":
        setIsUserSpeaking(true);
        isUserSpeakingRef.current = true;
        speechDetectedRef.current = true;
        if (pendingUserOrderRef.current === null) {
          pendingUserOrderRef.current = messageOrderRef.current++;
        }
        break;
        
      case "input_audio_buffer.speech_stopped":
        setIsUserSpeaking(false);
        isUserSpeakingRef.current = false;
        break;

      case "response.created":
        setIsAISpeaking(true);
        // Immediately stop treating user as speaking to prevent echo feedback
        setIsUserSpeaking(false);
        isUserSpeakingRef.current = false;
        if (unmutDelayRef.current) { clearTimeout(unmutDelayRef.current); unmutDelayRef.current = null; }
        setMicEnabled(false);
        if (dataChannelRef.current?.readyState === "open") {
          dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        }
        break;

      case "response.audio.delta":
      case "response.output_audio.delta":
        break;

      case "response.audio_transcript.delta":
      case "response.output_audio_transcript.delta":
        if (event.item_id && !pendingAITranscriptRef.current[event.item_id]) {
          pendingAITranscriptRef.current[event.item_id] = {
            content: event.delta || '',
            order: messageOrderRef.current++
          };
        } else if (event.item_id) {
          pendingAITranscriptRef.current[event.item_id].content += event.delta || '';
        }
        break;

      case "response.audio_transcript.done":
      case "response.output_audio_transcript.done":
        if (event.transcript) {
          const order = pendingAITranscriptRef.current[event.item_id]?.order ?? messageOrderRef.current++;
          if (event.item_id) delete pendingAITranscriptRef.current[event.item_id];

          // Defensive: strip any stray control token so it never shows/reads out
          const cleanContent = event.transcript.replace(/\[\s*hang\s*up\s*\]/ig, "").trim();

          if (cleanContent) {
            setMessages(prev => {
              const newMsg = { role: "assistant", content: cleanContent, timestamp: new Date().toISOString(), order, item_id: event.item_id };
              return [...prev, newMsg].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            });
          }

          // Fallback hang-up detection (primary path is the end_call tool)
          if (event.transcript.toLowerCase().includes("[hang up]")) {
            pendingHangupRef.current = true;
          }
        }
        break;

      case "response.audio.done":
      case "response.output_audio.done":
        setIsAISpeaking(false);
        delayedUnmute();
        break;

      case "response.done": {
        setIsAISpeaking(false);
        delayedUnmute();
        // Primary hang-up signal: the model invoked the end_call function tool
        const outputs = event.response?.output || [];
        const calledEndCall = outputs.some(
          (o) => o?.type === "function_call" && o?.name === "end_call"
        );
        if (calledEndCall) pendingHangupRef.current = true;

        if (pendingHangupRef.current && mountedRef.current) {
          pendingHangupRef.current = false;
          toast.info("Call ended", { description: "The client hung up" });
          // Let the AI's short goodbye line finish playing, then end the session
          // properly (save transcript + score + go to results). We can't detect
          // the end of a live WebRTC stream, so use a fixed grace period — shorter
          // when the model hung up without any spoken line (no audio to wait for).
          const hasSpokenLine = outputs.some((o) => o?.type === "message");
          setTimeout(() => {
            if (!mountedRef.current) return;
            if (handleEndRoleplayRef.current) {
              handleEndRoleplayRef.current();
            } else {
              disconnectCallRef.current?.();
            }
          }, hasSpokenLine ? 4000 : 800);
        }
        break;
      }

      case "response.cancelled":
        setIsAISpeaking(false);
        break;

      case "conversation.item.input_audio_transcription.completed":
        if (event.transcript) {
          const cleaned = event.transcript.trim().toLowerCase().replace(/[.!?,]/g, "");
          const wordCount = cleaned.split(/\s+/).filter(Boolean).length;
          
          // Aggressive filter in PTT mode: require at least 2 real words
          if (pushToTalkModeRef.current && (wordCount <= 1 || cleaned.length < 5)) {
            pendingUserOrderRef.current = null;
            break;
          }
          
          // Filter common garbage words in all modes
          const garbageWords = new Set([
            "you", "bye", "yeah", "hmm", "um", "uh", "ah", "oh",
            "okay", "ok", "mhm", "huh", "hey", "hi", "mm",
            "thank you", "thanks", "yes", "no", "yep", "nah"
          ]);
          if (garbageWords.has(cleaned) || cleaned.length < 3) {
            pendingUserOrderRef.current = null;
            break;
          }
          
          const order = pendingUserOrderRef.current ?? messageOrderRef.current++;
          pendingUserOrderRef.current = null;
          
          setMessages(prev => {
            const newMsg = { role: "user", content: event.transcript, timestamp: new Date().toISOString(), order, item_id: event.item_id };
            return [...prev, newMsg].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          });
        } else {
          pendingUserOrderRef.current = null;
        }
        break;

      case "error":
        toast.error("Error", { description: event.error?.message });
        break;

      default:
        break;
    }
  }, [setMicEnabled, delayedUnmute]);

  const initRealtimeSession = async (scenarioId) => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    
    if (!navigator.onLine) { handleConnectionError("No internet connection", "network_error"); return; }
    
    setConnectionState("connecting");
    setError(null);
    setErrorType(null);
    setConnectionProgress(10);
    
    connectionTimeoutRef.current = setTimeout(() => {
      if (connectionState === "connecting") { handleConnectionError("Connection timed out", "timeout"); cleanup(); }
    }, CONNECTION_TIMEOUT);
    
    let ephemeralKey = null;
    let newSessionId = null;
    let scenarioData = null;
    let realtimeModel = null;

    try {
      setConnectionProgress(20);
      const body = { scenario_id: scenarioId, session_id: rpSessionId, push_to_talk_mode: pushToTalkModeRef.current };
      if (isEmbedded && embedIdFromState) body.embed_id = embedIdFromState;
      
      const result = await safeFetch(`${API}/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      
      if (!result.ok) {
        const errData = result.json();
        if (result.status === 401) throw { message: "Session expired", type: "session_expired" };
        throw { message: errData.detail || `Error ${result.status}`, type: "openai_error" };
      }

      const sessionData = result.json();
      ephemeralKey = sessionData.client_secret?.value;
      newSessionId = sessionData.session_id;
      scenarioData = sessionData.scenario;
      realtimeModel = sessionData.model;
      
      if (!ephemeralKey) throw { message: "No token received", type: "openai_error" };
      setConnectionProgress(40);
    } catch (err) {
      handleConnectionError(err.message, err.type || "openai_error");
      return;
    }

    setRpSessionId(newSessionId);
    setScenario(scenarioData);

    let pc = null;
    
    try {
      setConnectionProgress(50);
      pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" }
        ]
      });
      peerConnectionRef.current = pc;

      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      document.body.appendChild(audioEl);
      audioElementRef.current = audioEl;

      // Capture remote stream for recording
      let remoteStream = null;
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
        remoteStream = e.streams[0];
        audioEl.play().catch(() => {});
        
        // Start audio recording after we have both streams
        if (localStreamRef.current && remoteStream) {
          startRecording(localStreamRef.current, remoteStream);
        }
      };

      pc.onconnectionstatechange = () => {
        if (!mountedRef.current) return;
        switch (pc.connectionState) {
          case "connected":
            setConnectionState("connected");
            setConnectionProgress(100);
            setReconnectAttempts(0);
            if (connectionTimeoutRef.current) { clearTimeout(connectionTimeoutRef.current); connectionTimeoutRef.current = null; }
            break;
          case "disconnected":
            toast.warning("Connection unstable", { description: "Attempting to recover..." });
            break;
          case "failed":
            handleConnectionError("Connection failed", "ice_failed");
            cleanup();
            break;
          case "closed":
            setConnectionState("disconnected");
            break;
          default: break;
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "failed") pc.restartIce();
      };

      setConnectionProgress(60);
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        });
      } catch (micError) {
        if (micError.name === "NotAllowedError" || micError.name === "PermissionDeniedError") {
          throw { message: "Microphone access denied", type: "microphone_denied" };
        } else if (micError.name === "NotFoundError" || micError.name === "DevicesNotFoundError") {
          throw { message: "No microphone found", type: "microphone_not_found" };
        } else {
          throw { message: micError.message, type: "microphone_denied" };
        }
      }
      
      localStreamRef.current = stream;
      
      if (pushToTalkMode) {
        const track = stream.getAudioTracks()[0];
        if (track) track.enabled = false;
      }
      
      pc.addTrack(stream.getTracks()[0]);
      setConnectionProgress(70);

      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;
      let initialGreetingSent = false;

      dc.onopen = () => {
        if (mountedRef.current) setConnectionState("connected");
        if (!initialGreetingSent) {
          initialGreetingSent = true;
          // If starting in PTT mode, disable server VAD immediately
          if (pushToTalkModeRef.current && dc.readyState === "open") {
            dc.send(JSON.stringify({
              type: "session.update",
              session: { audio: { input: { turn_detection: null } } }
            }));
          }
          setTimeout(() => {
            if (dc.readyState === "open") {
              dc.send(JSON.stringify({ type: "response.create", response: { output_modalities: ["audio"] } }));
            }
          }, 500);
        }
      };

      dc.onerror = (error) => console.error("Data channel error:", error);
      dc.onclose = () => {};
      dc.onmessage = (e) => { try { handleRealtimeEvent(JSON.parse(e.data)); } catch {} };

      setConnectionProgress(80);
    } catch (err) {
      handleConnectionError(err.message, err.type || "unknown");
      cleanup();
      return;
    }

    try {
      setConnectionProgress(90);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpAnswer = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        // Model comes from the backend session response so bumps only require a backend edit.
        const modelForSdp = encodeURIComponent(realtimeModel || "gpt-realtime-2.1");
        xhr.open("POST", `https://api.openai.com/v1/realtime/calls?model=${modelForSdp}`);
        xhr.setRequestHeader("Authorization", `Bearer ${ephemeralKey}`);
        xhr.setRequestHeader("Content-Type", "application/sdp");
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
          else reject({ message: `OpenAI error ${xhr.status}`, type: "openai_error" });
        };
        xhr.onerror = () => reject({ message: "Network error connecting to OpenAI", type: "network_error" });
        xhr.ontimeout = () => reject({ message: "OpenAI connection timed out", type: "timeout" });
        xhr.timeout = 30000;
        xhr.send(offer.sdp);
      });

      await pc.setRemoteDescription({ type: "answer", sdp: sdpAnswer });
      setConnectionProgress(100);
    } catch (err) {
      handleConnectionError(err.message, err.type || "openai_error");
      cleanup();
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (scenarioIdFromState) initRealtimeSession(scenarioIdFromState);
    return () => { mountedRef.current = false; cleanup(); };
    // Runs once on mount to establish the WebRTC session; re-running would tear
    // down and recreate the live call. Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = isMuted; setIsMuted(!isMuted); }
    }
  };

  const disconnectCall = () => {
    cleanup();
    setConnectionState("disconnected");
    setReconnectAttempts(0);
    initStartedRef.current = false;
  };

  const reconnectCall = () => {
    const id = scenario?.scenario_id || (isEmbedded ? scenario?.template_id : null) || scenarioIdFromState;
    if (id) {
      cleanup();
      initStartedRef.current = false;
      setError(null);
      setErrorType(null);
      initRealtimeSession(id);
    }
  };

  const resetAndReconnect = () => { setReconnectAttempts(0); reconnectCall(); };

  const handleEndRoleplay = async () => {
    if (!rpSessionId) return;
    setEnding(true);
    cleanup();

    // Upload recording in parallel with saving transcript
    const uploadPromise = uploadRecording(rpSessionId);

    try {
      if (messages.length > 0) {
        await safeFetch(`${API}/realtime/save-transcript`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: rpSessionId, messages })
        });
      }
    } catch {}

    try {
      const result = await safeFetch(`${API}/roleplay/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: rpSessionId })
      });
      if (result.ok) toast.success("Done!");
    } catch {}

    await uploadPromise;

    if (isEmbedded) {
      navigate(`/embed/${embedIdFromState}/results/${rpSessionId}`);
    } else {
      navigate(`/roleplay/results/${rpSessionId}`);
    }
    setEnding(false);
  };

  useEffect(() => {
    handleEndRoleplayRef.current = handleEndRoleplay;
    disconnectCallRef.current = disconnectCall;
  });

  const handleScreenShareToggle = useCallback((checked) => {
    setPushToTalkMode(checked);
    pushToTalkModeRef.current = checked;
    if (checked) {
      // Immediately disable mic
      setMicEnabled(false);
      setIsPushingToTalk(false);
      isPushingToTalkRef.current = false;
      speechDetectedRef.current = false;
      
      if (dataChannelRef.current?.readyState === "open") {
        // Clear any pending audio
        dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        // CRITICAL: Disable server VAD to prevent AI echo feedback loop.
        // GA gpt-realtime-2.x nests turn_detection under audio.input.
        dataChannelRef.current.send(JSON.stringify({
          type: "session.update",
          session: {
            audio: {
              input: {
                turn_detection: null
              }
            }
          }
        }));
      }
      toast.info("Screen Sharing Mode enabled", { description: "Hold the microphone button to speak" });
    } else {
      if (dataChannelRef.current?.readyState === "open") {
        dataChannelRef.current.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        // Re-enable server VAD for normal conversational mode.
        // GA gpt-realtime-2.x nests turn_detection under audio.input.
        dataChannelRef.current.send(JSON.stringify({
          type: "session.update",
          session: {
            audio: {
              input: {
                turn_detection: {
                  type: "server_vad",
                  threshold: 0.8,
                  prefix_padding_ms: 400,
                  silence_duration_ms: 1200,
                  create_response: true
                }
              }
            }
          }
        }));
      }
      setTimeout(() => {
        if (!isMuted && mountedRef.current && !isAISpeaking) {
          setMicEnabled(true);
        }
      }, 300);
      toast.info("Normal mode enabled");
    }
  }, [isMuted, isAISpeaking, setMicEnabled]);

  const visualizerState = connectionState !== "connected" ? "idle" 
    : isAISpeaking ? "speaking" 
    : isUserSpeaking ? "listening" 
    : "idle";

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid={isEmbedded ? "embedded-active-roleplay" : "active-roleplay-page"}>
      <header className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${
            connectionState === "connected" ? "bg-green-500 animate-pulse" :
            connectionState === "connecting" ? "bg-yellow-500 animate-pulse" : "bg-gray-400"
          }`} />
          <div>
            <h1 className="font-heading font-semibold text-lg">{scenario?.name || "Voice Roleplay"}</h1>
            <p className="text-sm text-muted-foreground">
              {connectionState === "connected" ? "Live" : connectionState === "connecting" ? "Connecting..." : "Disconnected"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isOnline && (
            <Badge variant="destructive" className="gap-1">
              <WifiOff className="w-3 h-3" /> Offline
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowTranscript(!showTranscript)} data-testid="toggle-transcript-btn">
            <MessageSquare className="w-4 h-4 mr-2" />
            {showTranscript ? "Hide" : "Show"}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col overflow-hidden ${showTranscript ? "lg:w-1/2" : "w-full"}`}>
          <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-hidden">
            {connectionState === "connecting" && connectionProgress > 0 && (
              <div className="mb-6 w-full max-w-xs">
                <Progress value={connectionProgress} className="h-2" />
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {connectionProgress < 40 ? "Getting session..." :
                   connectionProgress < 70 ? "Setting up audio..." :
                   connectionProgress < 90 ? "Connecting to AI..." : "Almost there..."}
                </p>
              </div>
            )}
            
            {error && (
              <div className="mb-8 p-4 rounded-lg bg-destructive/10 border border-destructive/20 max-w-md text-center">
                <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-2" />
                <p className="text-sm text-destructive mb-3">{error}</p>
                {errorType === "microphone_denied" && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Click the lock/camera icon in your browser's address bar to enable microphone access.
                  </p>
                )}
                {errorType === "network_error" && !isOnline && (
                  <p className="text-xs text-muted-foreground mb-3">Please check your internet connection.</p>
                )}
                {reconnectAttempts > 0 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Auto-reconnecting... Attempt {reconnectAttempts} of {MAX_RECONNECT_ATTEMPTS}
                  </p>
                )}
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={resetAndReconnect} disabled={!isOnline} data-testid="try-again-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                  </Button>
                  {!isEmbedded && (
                    <Button variant="ghost" size="sm" onClick={() => navigate("/roleplay/new")}>New Scenario</Button>
                  )}
                </div>
              </div>
            )}

            <div className="relative">
              <VoiceVisualizer state={visualizerState} volume={isUserSpeaking ? 80 : 0} />
              {connectionState === "connected" && (
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                  <Phone className="w-3 h-3 mr-1" /> Live
                </Badge>
              )}
            </div>

            <div className="mt-12 text-center">
              <p className={`font-medium ${isAISpeaking ? "text-primary" : isUserSpeaking ? "text-green-500" : "text-muted-foreground"}`}>
                {connectionState === "connecting" ? "Connecting..." :
                 connectionState === "connected" ? (isAISpeaking ? "AI speaking..." : isUserSpeaking ? "Listening..." : "Speak naturally") :
                 "Click Connect"}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex-shrink-0 p-6 border-t border-border bg-card/50 backdrop-blur-sm">
            {connectionState === "connected" && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <Monitor className="w-4 h-4 text-muted-foreground" />
                <Label htmlFor="ptt-mode" className="text-sm text-muted-foreground">Screen Sharing Mode</Label>
                <Switch id="ptt-mode" checked={pushToTalkMode} onCheckedChange={handleScreenShareToggle} data-testid="screen-share-toggle" />
              </div>
            )}
            
            <div className="flex items-center justify-center gap-4">
              {connectionState === "connected" ? (
                <>
                  {pushToTalkMode ? (
                    <Button 
                      variant={isPushingToTalk ? "default" : "outline"} 
                      size="lg" 
                      className={`w-20 h-20 rounded-full transition-all ${isPushingToTalk ? "bg-green-500 hover:bg-green-600 scale-110" : ""}`}
                      onMouseDown={handlePushToTalkStart}
                      onMouseUp={handlePushToTalkEnd}
                      onMouseLeave={handlePushToTalkEnd}
                      onTouchStart={handlePushToTalkStart}
                      onTouchEnd={handlePushToTalkEnd}
                      disabled={isMuted}
                      data-testid="ptt-button"
                    >
                      {isMuted ? <MicOff className="w-8 h-8 text-destructive" /> : isPushingToTalk ? <Mic className="w-8 h-8 animate-pulse" /> : <Mic className="w-8 h-8" />}
                    </Button>
                  ) : (
                    <Button variant="outline" size="lg" className="w-14 h-14 rounded-full" onClick={toggleMute} data-testid="mute-btn">
                      {isMuted ? <MicOff className="w-6 h-6 text-destructive" /> : <Mic className="w-6 h-6" />}
                    </Button>
                  )}
                  <Button size="lg" variant="destructive" className="w-14 h-14 rounded-full" onClick={disconnectCall} data-testid="disconnect-btn">
                    <PhoneOff className="w-6 h-6" />
                  </Button>
                </>
              ) : (
                <Button size="lg" className="bg-primary px-8" onClick={resetAndReconnect} disabled={connectionState === "connecting" || !isOnline} data-testid="connect-btn">
                  {connectionState === "connecting" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : !isOnline ? <WifiOff className="w-5 h-5 mr-2" /> : <Phone className="w-5 h-5 mr-2" />}
                  {connectionState === "connecting" ? "Connecting..." : !isOnline ? "No Connection" : "Connect"}
                </Button>
              )}
              {rpSessionId && (
                <Button size="lg" variant="destructive" className="px-6 h-14 rounded-full" onClick={handleEndRoleplay} disabled={ending} data-testid="end-roleplay-btn">
                  {ending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Square className="w-5 h-5 mr-2" />}
                  End Session
                </Button>
              )}
            </div>
            {pushToTalkMode && connectionState === "connected" && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                {isPushingToTalk ? "Release to send" : "Hold button to speak"}
              </p>
            )}
          </div>
        </div>

        {showTranscript && (
          <div className="hidden lg:flex w-1/2 border-l border-border flex-col bg-card h-full overflow-hidden">
            <div className="flex-shrink-0 p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-heading font-semibold">Transcript</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowTranscript(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div ref={transcriptContainerRef} className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <p className="text-center text-muted-foreground text-sm py-8" data-testid="transcript-empty">
                    {connectionState === "connected" ? "Waiting..." : "Connect to start"}
                  </p>
                ) : messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`transcript-msg-${i}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"
                    }`}>
                      <p className="text-xs font-medium mb-1 opacity-70">{msg.role === "user" ? "You" : "AI"}</p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
